import React, { useState, useEffect, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useGroupForm } from "../../hooks/useGroupForm";
import { descriptionPageSchema } from "../../Valdations/Schema/group.schema";
import FileUpload from "../../UploadFile/CustomIcd";
import styles from "./customIcdGroups.module.css";
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { CustomIcdAxiosInstance } from "../../Network/CustomIcd";
import { Datum } from "../../interface/CustomIcd";
import CodeMultiSelect from "../../component/CodeMultiSelect";
import { ICDOption } from "../../interface/IcdCode";
import type { CodeOption } from "../../component/SearchableCodeSelect";
import { AlertTriangle } from "lucide-react";
import { canRestoreOrHardDelete } from "../../interface/RoleHelpers";
import { isViewer } from "../../interface/RoleHelpers";

const CustomIcd: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Form Input State
  // =====================================
  const [codes, setCodes] = useState<CodeOption[]>([]);
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<CodeOption[]>([]);
  const [customIcdGroups, setCustomIcdGroups] = useState<CodeOption[]>([]);
  const [descriptions, setDescriptions] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Dialog & Modal Refs
  // =====================================
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);

  // =====================================
  // Form Control Helpers
  // =====================================
  const [submitted, setSubmitted] = useState(false);
  const isAtLeastOneSelected = (): boolean => codes.length > 0;
  const { values, errors, setField, validateAll } =
    useGroupForm(descriptionPageSchema);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | number | null>(null);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [IcdDialogOpen, setIcdDialogOpen] = useState(false);
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    type: "delete" | "toggle" | "update" | "hardDelete" | null;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({
    open: false,
    type: null,
    item: null,
    usageData: null,
    loadingUsage: false,
  });

  // =====================================
  // Search (server-side)
  // =====================================
  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    groupName: "",
    icdCode: "",
    status: "",
    isSoftDeleted: "false",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    groupName: "",
    icdCode: "",
    status: "",
    isSoftDeleted: "false",
  });

  // =====================================
  // Toast / Notification
  // =====================================
  const { toast } = useToast();

  // =====================================
  // Form Utilities
  // =====================================
  const clear = () => {
    setSubmitted(false);
    setField("name", "");
    setDescriptions("");
    setCodes([]);
    setMedicalIcdGroups([]);
    setCustomIcdGroups([]);
    setIsActive(true);
  };

  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await CustomIcdAxiosInstance.get(
        `custom-icd-group/${item.id}`,
        {
          params: { includeCodes: true },
        }
      );
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedItem(null);
    setSubmitted(false);
    setShowAllLogs(false);
    clear();
  };

  const handleAdd = () => {
    setEditIndex(null);
    setField("name", "");
    setDescriptions("");
    setCodes([]);
    setCustomIcdGroups([]);
    setMedicalIcdGroups([]);
    setIsDialogOpen(true);
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    if (error.response?.data?.error?.message)
      return error.response.data.error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.message) return error.message;
    return "An unexpected error occurred.";
  };

  // =====================================
  // Fetch Functions
  // =====================================
  const fetchIcdCodes = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      pageSize: number = 10
    ): Promise<{ data: CodeOption[]; total: number }> => {
      if (!searchTerm || searchTerm.trim().length < 3) {
        return { data: [], total: 0 };
      }

      try {
        const response = await CustomIcdAxiosInstance.get("medical-code/icd/", {
          params: { search: searchTerm, page, limit: pageSize },
        });

        const items: ICDOption[] = response.data?.data?.items ?? [];
        const total: number = response.data?.data?.total ?? items.length;

        return {
          data: items.map((item) => ({
            value: item.code,
            label: `${item.code} - ${item.description}`,
          })),
          total,
        };
      } catch (error) {
        toast({
          title: "Error",
          description: getApiErrorMessage(error),
          variant: "destructive",
        });

        return { data: [], total: 0 };
      }
    },
    []
  );

  const fetchMedicalCodes = async (
    searchTerm: string
  ): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1)
      return { data: [], total: 0 };
    const response = await CustomIcdAxiosInstance.get("medical-group/icd/", {
      params: { name: searchTerm, active: true },
    });
    const items = response.data?.data || [];
    return {
      data: items.map((group: any) => ({
        value: group.name,
        label: group.name,
      })),
      total: items.length,
    };
  };

  const fetchCustomCodes = async (
    searchTerm: string
  ): Promise<{ data: CodeOption[]; total: number }> => {
    if (!searchTerm || searchTerm.trim().length < 1)
      return { data: [], total: 0 };
    const response = await CustomIcdAxiosInstance.get("custom-icd-group", {
      params: { name: searchTerm, isActive: true },
    });
    const activeItems = (response.data?.data?.items ?? []).filter(
      (group: any) => group.isActive && !group.isSoftDeleted
    );
    return {
      data: activeItems.map((group: any) => ({
        value: group.name,
        label: group.name,
      })),
      total: activeItems.length,
    };
  };

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      includeCodes: true,
    };
    if (search) params.name = search;
    if (advancedSearch.groupName) params.name = advancedSearch.groupName;
    if (advancedSearch.icdCode) params.ICDs = advancedSearch.icdCode;
    if (advancedSearch.status) params.isActive = advancedSearch.status === "Active";
    params.isSoftDeleted = advancedSearch.isSoftDeleted === "true";
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.groupName ||
      advancedSearch.icdCode ||
      advancedSearch.status ||
      advancedSearch.isSoftDeleted === "true"
  );

  // =====================================
  // Fetch Items
  // =====================================
  const fetchItems = async () => {
    try {
      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await CustomIcdAxiosInstance.get("custom-icd-group", {
        params,
      });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
        setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    } 
  };

  // =====================================
  // CRUD Functions
  // =====================================
  const addItem = async (payload: {
    name: string;
    description: string;
    icdCodes: string[];
    medicalIcdCodes: string[];
    customIcdCodes: string[];
    isActive: boolean;
  }) => {
    try {
      if (payload.icdCodes.length === 0) {
        toast({
          title: "Error",
          description: "At least one ICD code is required.",
          variant: "destructive",
        });
        return;
      }
      const response = await CustomIcdAxiosInstance.post(
        "custom-icd-group",
        payload
      );
      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "New custom ICD group added successfully.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validateAll()) return;
    if (!isAtLeastOneSelected()) {
      toast({
        title: "Error",
        description: "At least one ICD code is required.",
        variant: "destructive",
      });
      return;
    }
    const payload = {
      name: values.name.trim(),
      description: descriptions.trim(),
      icdCodes: codes.map((c) => c.value),
      medicalIcdCodes: medicalIcdGroups.map((c) => c.value),
      customIcdCodes: customIcdGroups.map((c) => c.value),
      isActive,
    };
    await addItem(payload);
  };
const getCreatorName = (item: any): string | null => {
    if (!item) return null;
    if (item.createdBy) {
      const match = item.auditLog?.find(
        (log: any) => log.actorId === item.createdBy
      );
      if (match) return match.actorName;
      return item.createdBy;
    }
    return null;
  };
  const handleEdit = (item: any) => {
    setField("name", item.name);
    setDescriptions(item.description);
    setIsActive(item.isActive);
    setCodes(
      (item.icdCodes || []).map((code: string) => ({
        value: code,
        label: code,
      }))
    );
    setMedicalIcdGroups(
      (item.medicalIcdGroups || []).map(
        (obj: { id: number | string; name: string; isActive: boolean }) => ({
          value: obj.name,
          label: obj.name,
          isActive: obj.isActive,
        })
      )
    );
    setCustomIcdGroups(
      (item.customIcdGroups || []).map(
        (obj: {
          id: number | string;
          name: string;
          isActive: boolean;
          isSoftDeleted: boolean;
        }) => ({
          value: obj.name,
          label: obj.name,
          isActive: obj.isActive,
          isSoftDeleted: obj.isSoftDeleted ?? false,
        })
      )
    );

    setEditIndex(item.id);
    setIsDialogOpen(true);
  };

  const updateItem = async (id: string | number) => {
    setSubmitted(true);
    if (!validateAll()) return;
    if (!isAtLeastOneSelected()) {
      toast({
        title: "Error",
        description: "At least one ICD code is required.",
        variant: "destructive",
      });
      return;
    }

    const inactiveMedical = medicalIcdGroups
      .filter((g) => g.isActive === false)
      .map((g) => ({ label: g.label, groupType: "Medical", isActive: false }));

    const inactiveCustom = customIcdGroups
      .filter((g) => g.isActive === false || g.isSoftDeleted === true)
      .map((g) => ({
        label: g.label,
        groupType: "Custom",
        isActive: g.isActive,
        isSoftDeleted: g.isSoftDeleted ?? false,
      }));

    const allInactive = [...inactiveMedical, ...inactiveCustom];

    if (allInactive.length > 0) {
      setWarningDialog({
        open: true,
        type: "update",
        item: { id, allInactive } as any,
        usageData: null,
        loadingUsage: false,
      });
      return;
    }

    executeUpdate(id);
  };

  const executeUpdate = async (id: string | number) => {
    const payload = {
      name: values.name.trim(),
      description: descriptions.trim(),
      icdCodes: codes.map((c) => c.value),
      medicalIcdCodes: medicalIcdGroups.map((c) => c.value),
      customIcdCodes: customIcdGroups.map((c) => c.value),
      isActive,
    };
    try {
      const response = await CustomIcdAxiosInstance.put(
        `custom-icd-group/${id}`,
        payload
      );
      if (response.data?.success) {
        fetchItems();
        setIsDialogOpen(false);
        clear();
        toast({
          variant: "success",
          title: "Success",
          description: "Custom ICD group updated successfully.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    const item = allItems.find((i) => i.id === id) ?? null;
    if (!item) return;
    try {
      const res = await CustomIcdAxiosInstance.get(
        `custom-icd-group/usage/${id}`
      );
      const usageData = res.data.data;
      setWarningDialog({
        open: true,
        type: "delete",
        item,
        usageData,
        loadingUsage: false,
      });
    } catch (error) {
      setWarningDialog({
        open: true,
        type: "delete",
        item,
        usageData: { isUsed: false, totalUsageCount: 0 },
        loadingUsage: false,
      });
    }
  };

  const confirmDelete = async (id: string | number): Promise<void> => {
    try {
      await CustomIcdAxiosInstance.delete(
        `custom-icd-group/${id}/soft-delete`
      );
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group deleted successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const hardDeleteItem = async (id: string | number): Promise<void> => {
    try {
      await CustomIcdAxiosInstance.delete(`custom-icd-group/${id}`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group permanently deleted.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      await CustomIcdAxiosInstance.patch(`custom-icd-group/${id}/restore`);
      await fetchItems();
      toast({
        variant: "success",
        title: "Success",
        description: "Group restored successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (row: Datum) => {
    if (row.isSoftDeleted) return;

    if (row.isActive) {
      try {
        const res = await CustomIcdAxiosInstance.get(
          `custom-icd-group/usage/${row.id}`
        );
        const usageData = res.data.data;
        if (!usageData.isUsed || usageData.totalUsageCount === 0) {
          confirmToggle(row);
        } else {
          setWarningDialog({
            open: true,
            type: "toggle",
            item: row,
            usageData,
            loadingUsage: false,
          });
        }
      } catch (error) {
        console.error("Error fetching usage:", error);
      }
    } else {
      confirmToggle(row);
    }
  };

  const confirmToggle = async (row: Datum) => {
    const payload = {
      name: row.name,
      description: row.description,
      icdCodes: row.icdCodes,
      medicalIcdCodes: row.medicalIcdCodes,
      customIcdCodes: row.customIcdCodes,
      isActive: !row.isActive,
    };
    try {
      await CustomIcdAxiosInstance.put(`custom-icd-group/${row.id}`, payload);
      toast({
        variant: "success",
        title: "Success",
        description: `Status updated to ${!row.isActive ? "active" : "inactive"}`,
      });
      setAllItems((prev) =>
        prev.map((item) => {
          if (item.id === row.id) return { ...item, isActive: !item.isActive };
          return {
            ...item,
            medicalIcdGroups: item.medicalIcdGroups?.map((g) =>
              g.id === row.id ? { ...g, isActive: !row.isActive } : g
            ),
            customIcdGroups: item.customIcdGroups?.map((g) =>
              g.id === row.id ? { ...g, isActive: !row.isActive } : g
            ),
          };
        })
      );
    } catch (error: any) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const formatText = (text: string) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const cleanedIcd =
        advancedSearchInput.icdCode.trim().length >= 3
          ? advancedSearchInput.icdCode.trim()
          : "";

      setAdvancedSearch({
        groupName: advancedSearchInput.groupName.trim(),
        icdCode: cleanedIcd,
        status: advancedSearchInput.status,
        isSoftDeleted: advancedSearchInput.isSoftDeleted,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  useEffect(() => {
    fetchItems();
  }, [page, limit, search, advancedSearch]);

  // =====================================
  // Download Function
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await CustomIcdAxiosInstance.get(`custom-icd-group/${item.id}/export`, {
        responseType: 'arraybuffer',
      });

      const data = new Uint8Array(response.data);
      const wb = XLSX.read(data, { type: 'array' });
      const wsName = "Custom ICD Groups";
      const ws = wb.Sheets[wsName];

      if (ws) {
        ws['A2'] = { t: 's', v: item.name ?? "" };
        ws['B2'] = { t: 's', v: item.description ?? "" };
        ws['C2'] = { t: 's', v: (item.icdCodes ?? []).join(",") };
        ws['D2'] = { t: 's', v: (item.medicalIcdGroups ?? []).map((g) => g.name).join(",") };
        ws['E2'] = { t: 's', v: (item.customIcdGroups ?? []).map((g) => g.name).join(",") };
        ws['!ref'] = "A1:E2";
      }

      XLSX.writeFile(wb, `${item.name || "Custom_ICD_Group"}_Export.xlsx`);

      toast({
        title: "Success",
        description: "Exported template with data successfully. Ready to re-upload!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to generate export file.",
        variant: "destructive",
      });
    }
  };

  // =====================================
  // Table Columns
  // =====================================
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customIcd-table-header-name-sort-btn"
        >
          Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div
            data-testid={`customIcd-table-cell-name-${row.index}`}
            className={`text-center ${
              item.isSoftDeleted ? "line-through text-slate-400" : ""
            }`}
          >
            {row.getValue("name")}
          </div>
        );
      },
    },

    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customIcd-table-header-description-sort-btn"
        >
          Description
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div
            data-testid={`customIcd-table-cell-description-${row.index}`}
            className={`text-center ${
              item.isSoftDeleted ? "text-slate-400" : ""
            }`}
          >
            {row.getValue("description")}
          </div>
        );
      },
    },

    {
      id: "icdCodes",
      accessorFn: (row: Datum) => [
        ...(row.icdCodes || []),
        ...(row.medicalIcdGroups || []).map((g) => g.name),
        ...(row.customIcdGroups || []).map((g) => g.name),
      ],
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-black w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customIcd-table-header-icds-sort-btn"
        >
          ICDs
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isSoftDeleted = item.isSoftDeleted;

        const codesData = (item.icdCodes || []).map((code) => ({
          label: code,
          type: "SINGLE_CODE",
          isActive: true,
          isSoftDeleted: false,
        }));

        const medGroups = (item.medicalIcdGroups || []).map((g) => ({
          label: g.name,
          type: "MEDICAL_GROUP",
          isActive: g.isActive,
          isSoftDeleted: g.isSoftDeleted,
        }));

        const custGroups = (item.customIcdGroups || []).map((g) => ({
          label: g.name,
          type: "CUSTOM_GROUP",
          isActive: g.isActive,
          isSoftDeleted: g.isSoftDeleted,
        }));

        const icdData = [...codesData, ...medGroups, ...custGroups];
        if (icdData.length === 0)
          return <span className="text-slate-300" data-testid={`customIcd-table-cell-icds-empty-${row.index}`}>—</span>;

        const activeSearch =
          advancedSearch?.icdCode?.toLowerCase().trim() ||
          search?.toLowerCase().trim() ||
          "";
        const filteredIcds = activeSearch
          ? icdData.filter((i) => i.label.toLowerCase().includes(activeSearch))
          : icdData;
        const displayIcds = filteredIcds.slice(0, 2);
        const remainingCount = filteredIcds.length - 2;

        return (
          <div
            data-testid={`customIcd-table-cell-icds-${row.index}`}
            className={`flex flex-col items-center gap-1.5 py-1 ${
              isSoftDeleted ? "opacity-40" : ""
            }`}
          >
            <div className="flex flex-wrap justify-center items-center gap-2">
              {displayIcds.map((icd, idx) => {
                const isMatch =
                  activeSearch &&
                  icd.label.toLowerCase().includes(activeSearch);
                return (
                  <div
                    key={idx}
                    data-testid={`customIcd-table-cell-icd-badge-${row.index}-${idx}`}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 border transition-all
                      ${
                        icd.isSoftDeleted
                          ? "opacity-35 blur-[0.4px] line-through border-red-200 bg-red-50 text-red-500 decoration-red-400"
                          : !icd.isActive
                          ? "opacity-40 grayscale-[0.8] border-dashed bg-slate-100 text-slate-500"
                          : "bg-indigo-50 border-indigo-100 text-indigo-600"
                      }
                      ${isMatch ? "ring-2 ring-amber-400" : ""}
                    `}
                    title={icd.isSoftDeleted ? "Deleted Item" : !icd.isActive ? "Inactive Selection" : icd.label}
                  >
                    <i
                      className={`fa-solid ${
                        icd.isSoftDeleted
                          ? "fa-trash-can text-red-400"
                          : icd.type.includes("GROUP")
                          ? "fa-layer-group"
                          : "fa-tag"
                      } text-[9px]`}
                    ></i>
                    <span className="text-[11px] font-semibold">
                      {icd.label}
                    </span>
                    {!icd.isSoftDeleted && !icd.isActive && (
                      <i className="fa-solid fa-eye-slash text-[8px] ml-0.5"></i>
                    )}
                  </div>
                );
              })}
              {remainingCount > 0 && (
                <button
                  onClick={() => {
                    setActiveRowId(item.id);
                    setIcdDialogOpen(true);
                  }}
                  data-testid={`customIcd-table-cell-more-btn-${row.index}`}
                  className="px-2 py-1 rounded-md text-[10px] font-bold bg-white border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white transition-all"
                >
                  +{remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },

    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold text-black text-center normal-case">
          Status
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        if (item.isSoftDeleted) {
          return (
            <div className="w-full flex justify-center" data-testid={`customIcd-table-cell-status-disabled-${row.index}`}>
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-40 cursor-not-allowed">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
              </div>
            </div>
          );
        }
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => toggleActive(item)}
              data-testid={`customIcd-table-cell-status-toggle-${row.index}`}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },

    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="customIcd-table-header-view-sort-btn"
        >
          View
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              onClick={() => handleOpenDialog(item)}
              data-testid={`customIcd-table-cell-view-btn-${row.index}`}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="customIcd-page">
      <div className={styles.container} data-testid="customIcd-page-container">
        <h5 className={styles.title} data-testid="customIcd-page-title">
          <span
            className={styles.dialogIcon}
            data-testid="customIcd-page-title-icon"
          >
            <i className="fa-solid fa-file-medical-alt"></i>
          </span>
          Custom ICD Grouping
        </h5>
        <FileUpload
          fetchItems={fetchItems}
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* Search */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="customIcd-page-search-container"
      >
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]} data-testid="customIcd-page-search-header-info">
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>
            <button
              onClick={() => {
                setAdvancedSearchInput({
                  groupName: "",
                  icdCode: "",
                  status: "",
                  isSoftDeleted: "false",
                });
                setSearchInput("");
              }}
              data-testid="customIcd-page-search-reset-btn"
              className={styles["btn-clear-compact"]}
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          >
            <div
              className={styles["field-compact"]}
              data-testid="customIcd-page-search-groupname-field"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i>
                Group Name
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={advancedSearchInput.groupName}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      groupName: e.target.value,
                    })
                  }
                  data-testid="customIcd-page-search-groupname-input"
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            <div
              className={styles["field-compact"]}
              data-testid="customIcd-page-search-icdcode-field"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-code"></i>
                ICDs
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Search code..."
                  value={advancedSearchInput.icdCode}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      icdCode: e.target.value,
                    })
                  }
                  data-testid="customIcd-page-search-icdcode-input"
                  className={styles["input-compact"]}
                />
              </div>
            </div>

            <div
              className={styles["field-compact"]}
              data-testid="customIcd-page-search-status-field"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.status}
                  onChange={(e) =>
                    setAdvancedSearchInput({
                      ...advancedSearchInput,
                      status: e.target.value,
                    })
                  }
                  data-testid="customIcd-page-search-status-select"
                  className={styles["input-compact"]}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* delete */}
            <div
              className={styles["field-compact"]}
              data-testid="customIcd-page-search-soft-deleted-field"
            >
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-trash-can"></i>
                Deleted Groups
              </label>
              <button
                type="button"
                data-testid="customIcd-page-search-soft-deleted-btn"
                onClick={() =>
                  setAdvancedSearchInput({
                    ...advancedSearchInput,
                    isSoftDeleted:
                      advancedSearchInput.isSoftDeleted === "true"
                        ? "false"
                        : "true",
                  })
                }
                className={`${styles["input-compact"]} w-full flex items-center justify-center gap-2 cursor-pointer select-none transition-all ${
                  advancedSearchInput.isSoftDeleted === "true"
                    ? "!bg-red-500 !border-red-600 !text-white font-semibold"
                    : "hover:bg-slate-50 text-slate-600"
                }`}
              >
                <i
                  className={`fa-solid ${
                    advancedSearchInput.isSoftDeleted === "true"
                      ? "fa-rotate-left"
                      : "fa-eye-slash"
                  } text-xs`}
                ></i>
                <span className="text-xs">
                  {advancedSearchInput.isSoftDeleted === "true"
                    ? "Back to List"
                    : "Show "}
                </span>
              </button>
            </div>
          </div>

          <div className={styles["stats-compact"]} data-testid="customIcd-page-search-stats">
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]}>
              Showing <strong>{allItems.length}</strong> of{" "}
              <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.groupName ||
              advancedSearch.icdCode ||
              advancedSearch.status ||
              advancedSearch.isSoftDeleted == "true") && (
              <span
                className={styles["badge-filtered-compact"]}
                data-testid="customIcd-page-search-filtered-badge"
              >
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* View Dialog */}
      {isViewDialogOpen && selectedItem && (
        <dialog
          className={styles["modern-dialog"]}
          open
          data-testid="customIcd-page-dialog"
        >
          <div
            className={styles["dialog-container"]}
            data-testid="customIcd-page-dialog-container"
          >
            <div
              className={styles["dialog-header"]}
              data-testid="customIcd-page-dialog-header"
            >
              <div className={styles["header-content"]}>
                <div
                  className={styles["header-icon"]}
                  data-testid="customIcd-page-dialog-header-icon"
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div>
                  <h2
                    className={styles["dialog-title"]}
                    data-testid="customIcd-page-dialog-title"
                  >
                    Group Details
                  </h2>
                  <p
                    className={styles["dialog-subtitle"]}
                    data-testid="customIcd-page-dialog-subtitle"
                  >
                    {selectedItem.name}
                  </p>
                </div>
              </div>
              <button
                className={styles["close-btn"]}
                onClick={handleCloseDialog}
                data-testid="customIcd-page-dialog-close-btn"
              >
                ✕
              </button>
            </div>

            <div
              className={styles["dialog-body"]}
              data-testid="customIcd-page-dialog-body"
            >
              <section
                className={styles.section}
                data-testid="customIcd-page-dialog-basic-info"
              >
                <h3 className={styles["section-title"]}>Basic Information</h3>
                <div className={styles["info-group"]}>
                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-id"
                  >
                    <span className={styles["row-label"]}>Group ID</span>
                    <span
                      className={styles["info-text"]}
                      data-testid="customIcd-page-dialog-text-id"
                    >
                      {selectedItem
                        ? (page - 1) * limit +
                          allItems.findIndex((i) => i.id === selectedItem.id) +
                          1
                        : ""}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-name"
                  >
                    <span className={styles["row-label"]}>Group Name</span>
                    <span
                      className={styles["info-text"]}
                      data-testid="customIcd-page-dialog-text-name"
                    >
                      {selectedItem.name}
                    </span>
                  </div>
                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-description"
                  >
                    <span className={styles["row-label"]}>Description</span>
                    <span
                      className={styles["info-text"]}
                      data-testid="customIcd-page-dialog-text-description"
                    >
                      {selectedItem.description}
                    </span>
                  </div>

                  {/* 1. Medical ICD Group Section */}
                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-medical-groups"
                  >
                    <span className={styles["row-label"]}>
                      Medical ICD Group
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.medicalIcdGroups?.length
                        ? selectedItem.medicalIcdGroups.map((group, idx) => {
                            const badgeClasses = [
                              styles["group-badge-v2"],
                              !group.isActive
                                ? styles["badge-inactive"]
                                : styles["badge-active"],
                              group.isSoftDeleted
                                ? styles["badge-soft-deleted"]
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ");

                            return (
                              <div
                                key={idx}
                                className={badgeClasses}
                                data-testid={`customIcd-page-dialog-medical-group-${idx}`}
                              >
                                {group.isSoftDeleted ? (
                                  <i className="fa-solid fa-trash-can mr-2 text-xs text-red-500"></i>
                                ) : !group.isActive ? (
                                  <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                                ) : null}

                                <span className={styles["group-name"]}>
                                  {group.name}
                                </span>

                                {group.isSoftDeleted ? (
                                  <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1.5 tracking-wide scale-90">
                                    Deleted
                                  </span>
                                ) : !group.isActive ? (
                                  <span className={styles["status-text-mini"]}>
                                    Inactive
                                  </span>
                                ) : null}
                              </div>
                            );
                          })
                        : "—"}
                    </div>
                  </div>

                  {/* 2. Custom ICD Group Section */}
                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-custom-groups"
                  >
                    <span className={styles["row-label"]}>
                      Custom ICD Group
                    </span>
                    <div className="flex flex-wrap gap-3">
                      {selectedItem.customIcdGroups?.length
                        ? selectedItem.customIcdGroups.map((group, idx) => {
                            const softDeleteClasses = group.isSoftDeleted
                              ? "opacity-35 blur-[0.4px] line-through decoration-red-400 border-dashed bg-red-50 text-red-500"
                              : !group.isActive
                              ? styles["badge-inactive"]
                              : styles["badge-active"];

                            return (
                              <div
                                key={idx}
                                data-testid={`customIcd-page-dialog-custom-group-${idx}`}
                                className={`${styles["group-badge-v2"]} ${softDeleteClasses}`}
                              >
                                {group.isSoftDeleted ? (
                                  <i className="fa-solid fa-trash-can mr-2 text-xs text-red-500"></i>
                                ) : !group.isActive ? (
                                  <i className="fa-solid fa-eye-slash mr-2 text-xs"></i>
                                ) : null}

                                <span className={styles["group-name"]}>
                                  {group.name}
                                </span>

                                {group.isSoftDeleted ? (
                                  <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 rounded ml-1.5 tracking-wide">
                                    Deleted
                                  </span>
                                ) : !group.isActive ? (
                                  <span className={styles["status-text-mini"]}>
                                    Inactive
                                  </span>
                                ) : null}
                              </div>
                            );
                          })
                        : "—"}
                    </div>
                  </div>

                  <div
                    className={styles["info-row"]}
                    data-testid="customIcd-page-dialog-row-status"
                  >
                    <span className={styles["row-label"]}>Status</span>
                    <div
                      className={`${styles["status-badge-inline"]} ${
                        selectedItem.isActive ? styles.active : styles.inactive
                      }`}
                      data-testid="customIcd-page-dialog-status-badge"
                    >
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              <section
                className={`${styles.section} ${styles["full-width"]}`}
                data-testid="customIcd-page-dialog-codes-section"
              >
                <div className={styles["section-header-flex"]}>
                  <h3 className={styles["section-title"]}>ICD Codes</h3>
                  <span
                    className={styles["count-badge"]}
                    data-testid="customIcd-page-dialog-codes-count"
                  >
                    {selectedItem.icdCodes?.length || 0}
                  </span>
                </div>
                <div
                  className={styles["Icd-scroll-area"]}
                  data-testid="customIcd-page-dialog-codes-scroll"
                >
                  {selectedItem.icdCodes?.length ? (
                    <div
                      className={styles["Icd-grid"]}
                      data-testid="customIcd-page-dialog-codes-grid"
                    >
                      {selectedItem.icdCodes.map((code, idx) => (
                        <div
                          key={idx}
                          className={styles["Icd-card-mini"]}
                          data-testid={`customIcd-page-dialog-code-${idx}`}
                        >
                          <div className={styles["mini-icon"]}>#</div>
                          <span className={styles["Icd-name"]}>{code}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className={styles["empty-state-mini"]}
                      data-testid="customIcd-page-dialog-codes-empty"
                    >
                      No ICDs Assigned
                    </div>
                  )}
                </div>

                {/* ── Audit Trail / Logs ── */}
                <div
                  style={{ padding: "20px 28px 8px" }}
                  data-testid="customIcd-page-dialog-audit-trail"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: "#f0fdf4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <i
                        className="fa-solid fa-clock-rotate-left"
                        style={{ fontSize: 12, color: "#16a34a" }}
                      />
                    </div>
                    <span
                      style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}
                    >
                      Audit Trail
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      data-testid="customIcd-page-dialog-audit-createdby"
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#94a3b8",
                        }}
                      >
                        Created By
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginTop: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #3b82f6, #2563eb)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#fff",
                          }}
                        >
                          {String(getCreatorName(selectedItem) ?? "?")[0].toUpperCase()}
                        </div>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {getCreatorName(selectedItem) || "—"}
                        </span>
                      </div>
                    </div>

                    <div
                      data-testid="customIcd-page-dialog-audit-createdat"
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#94a3b8",
                        }}
                      >
                        Created On
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 10,
                        }}
                      >
                        <i
                          className="fa-regular fa-calendar"
                          style={{ fontSize: 14, color: "#3b82f6" }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {(selectedItem as any).createdAt
                            ? new Date(
                                (selectedItem as any).createdAt
                              ).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </span>
                      </div>
                    </div>

                    <div
                      data-testid="customIcd-page-dialog-audit-updatedat"
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#94a3b8",
                        }}
                      >
                        Last Updated
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 10,
                        }}
                      >
                        <i
                          className="fa-regular fa-clock"
                          style={{ fontSize: 14, color: "#10b981" }}
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {(selectedItem as any).updatedAt
                            ? new Date(
                                (selectedItem as any).updatedAt
                              ).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(selectedItem as any).auditLog?.length > 0 && (
                    <div
                      data-testid="customIcd-page-dialog-audit-history"
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 12,
                        }}
                      >
                        <i
                          className="fa-solid fa-pen-to-square"
                          style={{ fontSize: 11, color: "#10b981" }}
                        />
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#94a3b8",
                            textTransform: "uppercase",
                          }}
                        >
                          Update History
                        </span>
                        <span
                          data-testid="customIcd-page-dialog-audit-count-badge"
                          style={{
                            fontSize: 10,
                            padding: "1px 8px",
                            borderRadius: 9999,
                            background: "#f0fdf4",
                            color: "#15803d",
                            fontWeight: 600,
                            border: "1px solid #bbf7d0",
                            marginLeft: "auto",
                          }}
                        >
                          {(selectedItem as any).auditLog.length} update
                          {(selectedItem as any).auditLog.length !== 1
                            ? "s"
                            : ""}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {(selectedItem as any).auditLog
                          .slice(0, showAllLogs ? undefined : 4)
                          .map((log: any, i: number) => (
                            <div
                              key={log.id}
                              data-testid={`customIcd-page-dialog-audit-item-${i}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "8px 12px",
                                borderRadius: 8,
                                background: i === 0 ? "#f0fdf4" : "#f8fafc",
                                border: `1px solid ${
                                  i === 0 ? "#bbf7d0" : "#e2e8f0"
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    background:
                                      i === 0
                                        ? "linear-gradient(135deg, #10b981, #059669)"
                                        : "linear-gradient(135deg, #94a3b8, #64748b)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#fff",
                                  }}
                                >
                                  {String(log.actorName ?? "?")[0].toUpperCase()}
                                </div>
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#1e293b",
                                  }}
                                >
                                  {log.actorName}
                                </span>
                                {i === 0 && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      padding: "1px 8px",
                                      borderRadius: 9999,
                                      background: "#dcfce7",
                                      color: "#15803d",
                                      fontWeight: 600,
                                      border: "1px solid #bbf7d0",
                                    }}
                                  >
                                    Latest
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                {new Date(log.createdAt).toLocaleString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </span>
                            </div>
                          ))}

                        {(selectedItem as any).auditLog.length > 4 && (
                          <div style={{ textAlign: "center", padding: "6px 0" }}>
                            <button
                              onClick={() => setShowAllLogs((v) => !v)}
                              data-testid="customIcd-page-dialog-audit-showmore-btn"
                              style={{
                                fontSize: 11,
                                color: "#3b82f6",
                                fontWeight: 600,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              {showAllLogs
                                ? "Show less ↑"
                                : `+${(selectedItem as any).auditLog.length - 4} more updates ↓`}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div
              className={styles["dialog-footer"]}
              data-testid="customIcd-page-dialog-footer"
            >
              <button
                className={styles["btn-secondary"]}
                onClick={handleCloseDialog}
                data-testid="customIcd-page-dialog-footer-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* ICD Details Dialog */}
      <Dialog open={IcdDialogOpen} onOpenChange={setIcdDialogOpen}>
        {(() => {
          const activeItem = allItems.find((item) => item.id === activeRowId);
          return (
            <DialogContent
              data-testid="customIcd-page-icd-dialog-content"
              className="max-w-lg modern-dialog bg-white icd-dialog flex flex-col max-h-[90vh]"
            >
              <DialogHeader className="flex-shrink-0 border-b pb-3">
                <DialogTitle
                  data-testid="customIcd-page-icd-dialog-title"
                  className="text-xl font-bold text-blue-900 flex items-center gap-2"
                >
                  <i className="fa-solid fa-list-check text-blue-600"></i>
                  Group : {activeItem?.name}
                </DialogTitle>
              </DialogHeader>

              <div
                className="mt-4 dialog-body overflow-y-auto pr-2 custom-scrollbar flex-1"
                data-testid="customIcd-page-icd-dialog-body"
              >
                <div className="flex flex-col gap-6">
                  {activeItem?.icdCodes && activeItem.icdCodes.length > 0 && (
                    <div className="space-y-3" data-testid="customIcd-page-icd-dialog-codes-section">
                      <h4 className="text-[11px] font-bold text-slate-500 flex items-center gap-2 ml-1">
                        <i className="fa-solid fa-tag text-indigo-500"></i>
                        ICD Codes
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {activeItem.icdCodes.map((code, index) => (
                          <div
                            key={index}
                            data-testid={`customIcd-page-icd-dialog-code-item-${index}`}
                            className="flex items-center gap-2 p-2 bg-indigo-50/50 border border-indigo-100 rounded-lg"
                          >
                            <span className="text-[10px] font-bold text-indigo-400">
                              #
                            </span>
                            <span className="text-sm font-semibold text-indigo-900">
                              {code}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeItem?.medicalIcdGroups &&
                    activeItem.medicalIcdGroups.length > 0 && (
                      <div className="space-y-3" data-testid="customIcd-page-icd-dialog-med-groups-section">
                        <h4 className="text-[11px] font-bold text-slate-500 flex items-center gap-2 ml-1">
                          <i className="fa-solid fa-layer-group text-emerald-500"></i>
                          Medical Groups
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {activeItem.medicalIcdGroups.map(
                            (group, index: number) => (
                              <div
                                key={index}
                                data-testid={`customIcd-page-icd-dialog-med-group-item-${index}`}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                                  !group.isActive
                                    ? "bg-slate-50 opacity-60 grayscale border-slate-200 border-dashed"
                                    : "bg-emerald-50/50 border-emerald-100"
                                }`}
                              >
                                <div
                                  className={`flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 ${
                                    !group.isActive
                                      ? "bg-slate-400"
                                      : "bg-emerald-600"
                                  } text-white shadow-sm`}
                                >
                                  <i className="fa-solid fa-layer-group text-[10px]"></i>
                                </div>
                                <span className="text-gray-800 text-sm font-semibold flex-1">
                                  {group.name}
                                </span>
                                {!group.isActive && (
                                  <span className="text-[9px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded flex items-center gap-1">
                                    <i className="fa-solid fa-eye-slash"></i>{" "}
                                    INACTIVE
                                  </span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {activeItem?.customIcdGroups &&
                    activeItem.customIcdGroups.length > 0 && (
                      <div className="space-y-3" data-testid="customIcd-page-icd-dialog-custom-groups-section">
                        <h4 className="text-[11px] font-bold text-slate-500 flex items-center gap-2 ml-1">
                          <i className="fa-solid fa-folder-tree text-violet-500"></i>
                          Custom Groups
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {activeItem.customIcdGroups.map(
                            (group, index: number) => {
                              const isDeleted = group.isSoftDeleted;
                              const isInactive = !group.isActive;

                              return (
                                <div
                                  key={index}
                                  data-testid={`customIcd-page-icd-dialog-custom-group-item-${index}`}
                                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                                    isDeleted
                                      ? "bg-red-50/40 opacity-45 blur-[0.3px] line-through border-red-200 text-red-500 decoration-red-400"
                                      : isInactive
                                      ? "bg-slate-50 opacity-60 grayscale border-slate-200 border-dashed"
                                      : "bg-violet-50/50 border-violet-100"
                                  }`}
                                >
                                  <div
                                    className={`flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 text-white shadow-sm transition-all ${
                                      isDeleted
                                        ? "bg-red-500"
                                        : isInactive
                                        ? "bg-slate-400"
                                        : "bg-violet-600"
                                    }`}
                                  >
                                    <i
                                      className={`fa-solid ${
                                        isDeleted
                                          ? "fa-trash-can"
                                          : "fa-folder-tree"
                                      } text-[10px]`}
                                    ></i>
                                  </div>

                                  <span
                                    className={`text-sm font-semibold flex-1 ${
                                      isDeleted
                                        ? "text-red-900"
                                        : "text-gray-800"
                                    }`}
                                  >
                                    {group.name}
                                  </span>

                                  {isDeleted ? (
                                    <span className="text-[9px] font-extrabold bg-red-100 text-red-600 px-2 py-0.5 rounded flex items-center gap-1 tracking-wider border border-red-200">
                                      <i className="fa-solid fa-trash-can"></i>{" "}
                                      DELETED
                                    </span>
                                  ) : (
                                    isInactive && (
                                      <span className="text-[9px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded flex items-center gap-1">
                                        <i className="fa-solid fa-eye-slash"></i>{" "}
                                        INACTIVE
                                      </span>
                                    )
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-slate-50/50 -mx-6 -mb-6 px-8 py-4 rounded-b-2xl">
                <div className="flex items-center gap-6">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[9px] text-slate-400 font-black tracking-widest leading-none">
                      Total codes
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                      <span
                        data-testid="customIcd-page-icd-dialog-total-codes"
                        className="text-base font-bold text-slate-700 leading-none"
                      >
                        {activeItem?.icdCodes?.length || 0}
                      </span>
                    </div>
                  </div>
                  <div className="h-8 w-[1px] bg-slate-200"></div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[9px] text-slate-400 font-black tracking-widest leading-none">
                      Total Groups
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      <span
                        data-testid="customIcd-page-icd-dialog-total-groups"
                        className="text-base font-bold text-slate-700 leading-none"
                      >
                        {(activeItem?.medicalIcdGroups?.length || 0) +
                          (activeItem?.customIcdGroups?.length || 0)}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setIcdDialogOpen(false)}
                  data-testid="customIcd-page-icd-dialog-close-btn"
                  className="bg-slate-900 hover:bg-black text-white h-9 px-6 rounded-lg text-[11px] font-bold"
                >
                  Close
                </Button>
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clear();
            setEditIndex(null);
          }
          setIsDialogOpen(open);
        }}
      >
        <DialogContent
          className={styles.dialogContent}
          data-testid="customIcd-page-dialog-content"
        >
          <DialogHeader className={styles.dialogHeader}>
            <DialogTitle
              className={styles.dialogTitle}
              data-testid="customIcd-page-form-dialog-title"
            >
              {editIndex === null
                ? "Add Custom ICD Group"
                : "Edit Custom ICD Group"}
            </DialogTitle>
            <DialogDescription
              className={styles.dialogDescription}
              data-testid="customIcd-page-form-dialog-desc"
            >
              Fill in the details below to{" "}
              {editIndex === null ? "create" : "update"} a Custom ICD Group.
            </DialogDescription>
          </DialogHeader>

          <div
            className={styles.dialogBody}
            data-testid="customIcd-page-form-dialog-body"
          >
            <div className={styles.formGrid}>
              <div>
                <div
                  className={styles.formGroup}
                  data-testid="customIcd-page-input-groupname-group"
                >
                  <Label htmlFor="name">Name :</Label>
                  <Input
                    id="name"
                    placeholder="Enter Name..."
                    value={values.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="customIcd-page-form-name-input"
                  />
                  {submitted && errors.name && (
                    <span
                      className={styles.error}
                      data-testid="customIcd-page-form-name-error"
                    >
                      {errors.name}
                    </span>
                  )}
                </div>
                <div
                  className={styles.formGroup}
                  data-testid="customIcd-page-input-icdcode-group"
                >
                  <Label className={styles.label} htmlFor="icdCodes">
                    ICD Code
                  </Label>
                  <div style={{ maxWidth: 500 }} data-testid="customIcd-page-form-icdcodes-select">
                    <CodeMultiSelect
                      fetchOptions={fetchIcdCodes}
                      value={codes}
                      onChange={setCodes}
                      placeholder="Type at least 3 letters to search..."
                    />
                  </div>
                </div>
                <div
                  className={styles.formGroup}
                  data-testid="customIcd-page-input-customIcdGroup-group"
                >
                  <Label className={styles.label} htmlFor="customIcdCodes">
                    Custom ICD Group
                  </Label>
                  <div style={{ maxWidth: 500 }} data-testid="customIcd-page-form-custom-groups-select">
                    <CodeMultiSelect
                      fetchOptions={fetchCustomCodes}
                      value={customIcdGroups}
                      onChange={setCustomIcdGroups}
                      placeholder="Type at least 3 letters to search..."
                    />
                  </div>
                </div>
              </div>
              <div>
                <div
                  className={styles.formGroup}
                  data-testid="customIcd-page-input-description-group"
                >
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    placeholder="Description..."
                    value={descriptions}
                    onChange={(e) => setDescriptions(e.target.value)}
                    data-testid="customIcd-page-form-description-input"
                  />
                </div>
                <div
                  className={styles.formGroup}
                  data-testid="customIcd-page-input-medicalIcdGroup-group"
                >
                  <Label className={styles.label}>Medical ICD Group</Label>
                  <div data-testid="customIcd-page-form-medical-groups-select">
                    <CodeMultiSelect
                      fetchOptions={fetchMedicalCodes}
                      value={medicalIcdGroups}
                      onChange={setMedicalIcdGroups}
                      placeholder="Type at least 3 letters to search..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className={styles.dialogFooter}>
            <Button
              onClick={() => {
                if (editIndex === null) {
                  handleSubmit();
                } else {
                  updateItem(editIndex);
                }
              }}
              data-testid="customIcd-page-form-submit-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editIndex === null ? "Add Group" : "Update Group"}
            </Button>
            <Button
              className={styles.cancel}
              variant="outline"
              data-testid="customIcd-page-form-cancel-btn"
              onClick={() => {
                setIsDialogOpen(false);
                setEditIndex(null);
                setSubmitted(false);
                clear();
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning / Confirm Dialog */}
      <Dialog
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open &&
          setWarningDialog({
            open: false,
            type: null,
            item: null,
            usageData: null,
            loadingUsage: false,
          })
        }
      >
        <DialogContent
          className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
          data-testid="customIcd-page-warning-dialog-content"
        >
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle
                data-testid="customIcd-page-warning-dialog-title"
                className={`flex items-center gap-2 font-bold ${
                  warningDialog.type === "delete" ||
                  warningDialog.type === "hardDelete"
                    ? "text-red-600"
                    : "text-amber-600"
                }`}
              >
                <AlertTriangle className="h-5 w-5" />
                {warningDialog.type === "delete"
                  ? "Confirm deletion"
                  : warningDialog.type === "hardDelete"
                  ? "Permanent deletion"
                  : "Warning: inactive groups selected"}
              </DialogTitle>
              <DialogDescription
                data-testid="customIcd-page-warning-dialog-desc"
                className="text-slate-500 text-sm"
              >
                {warningDialog.type === "delete"
                  ? "Are you sure you want to delete this group? This action cannot be undone."
                  : warningDialog.type === "hardDelete"
                  ? "This will permanently remove the group from the system. This action cannot be undone."
                  : "Some selected groups are currently inactive or deleted. Do you still want to proceed?"}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div
            className="px-6 flex-1 min-h-0 flex flex-col overflow-y-auto"
            data-testid="customIcd-page-warning-dialog-body"
          >
            {/* Hard Delete Warning */}
            {warningDialog.type === "hardDelete" && (
              <div
                className="py-6 flex flex-col items-center justify-center gap-3"
                data-testid="customIcd-page-warning-hard-delete-info"
              >
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border-2 border-red-100">
                  <i className="fa-solid fa-trash-can text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-slate-600 text-center px-4">
                  The group{" "}
                  <strong className="text-slate-800">
                    "{warningDialog.item?.name}"
                  </strong>{" "}
                  will be permanently deleted. This cannot be undone.
                </p>
              </div>
            )}

            {/* Update Warning */}
            {warningDialog.type === "update" && (
              <div
                className="space-y-3 mb-4"
                data-testid="customIcd-page-warning-update-info"
              >
                <p className="text-xs font-semibold text-slate-500 tracking-wider">
                  Inactive or deleted groups:
                </p>
                {(warningDialog.item as any)?.allInactive?.map(
                  (group: any, idx: number) => (
                    <div
                      key={idx}
                      data-testid={`customIcd-page-warning-inactive-item-${idx}`}
                      className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-slate-400 tracking-tight">
                          {group.groupType} Group
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            group.isSoftDeleted
                              ? "line-through text-slate-400"
                              : "text-slate-700"
                          }`}
                        >
                          {group.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {group.isSoftDeleted ? (
                          <div className="flex items-center gap-1.5 text-red-500 bg-red-50 px-2 py-1 rounded-md border border-red-100">
                            <i className="fa-solid fa-trash-can text-[10px]"></i>
                            <span className="text-[10px] font-bold">
                              Deleted
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-amber-500 bg-amber-50 px-2 py-1 rounded-md border border-amber-100">
                            <i className="fa-solid fa-eye-slash text-[10px]"></i>
                            <span className="text-[10px] font-bold">
                              Inactive
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
                <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-100 mt-4 italic">
                  * Note: these groups are currently disabled or soft-deleted
                  in the system.
                </p>
              </div>
            )}

            {/* Delete / Toggle Warning */}
            {warningDialog.type !== "update" &&
              warningDialog.type !== "hardDelete" &&
              (warningDialog.usageData?.isUsed ||
              warningDialog.usageData?.totalUsageCount > 0 ? (
                <div
                  className={styles.alertContainer}
                  data-testid="customIcd-page-warning-usage-container"
                >
                  <div className="rounded-lg bg-amber-50 p-3 border border-amber-100 flex-shrink-0">
                    <p className="text-xs text-amber-800 font-medium text-center">
                      Currently active in{" "}
                      <strong>
                        {warningDialog.usageData.totalUsageCount}
                      </strong>{" "}
                      location(s).
                    </p>
                  </div>
                  <div className={styles.alertScrollArea}>
                    {warningDialog.usageData.icdValidationRules?.length >
                      0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customIcd-page-warning-icd-rules"
                      >
                        <h4 className={styles.alertSectionTitle}>
                          ICD Rules
                        </h4>
                        {warningDialog.usageData.icdValidationRules.map(
                          (rule: any, idx: number) => (
                            <div
                              key={rule.ruleId || idx}
                              data-testid={`customIcd-page-warning-icd-rule-${idx}`}
                              className={styles.alertCard}
                            >
                              <span className="text-sm font-medium">
                                {rule.ruleName}
                              </span>
                              <span className={styles.alertBadge}>
                                {formatText(rule.ruleType)}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                    {warningDialog.usageData.activityValidationRules?.length >
                      0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customIcd-page-warning-activity-rules"
                      >
                        <h4 className={styles.alertSectionTitle}>
                          Activity Rules
                        </h4>
                        {warningDialog.usageData.activityValidationRules.map(
                          (rule: any, idx: number) => (
                            <div
                              key={rule.ruleId || idx}
                              data-testid={`customIcd-page-warning-activity-rule-${idx}`}
                              className={styles.alertActivityCard}
                            >
                              <span className="text-sm font-medium">
                                {rule.ruleName}
                              </span>
                              <span className={styles.alertActivityBadge}>
                                {formatText(rule.ruleType)}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                    {warningDialog.usageData.relatedByGroups?.length > 0 && (
                      <div
                        className={styles.alertSection}
                        data-testid="customIcd-page-warning-related-groups"
                      >
                        <h4 className={styles.alertSectionTitle}>
                          Related Groups
                        </h4>
                        {warningDialog.usageData.relatedByGroups.map(
                          (group: any, idx: number) => (
                            <div
                              key={group.groupId || group.id || idx}
                              data-testid={`customIcd-page-warning-related-group-${idx}`}
                              className={styles.alertCustomGroupContainer}
                            >
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                                <span>
                                  Group:{" "}
                                  <strong>
                                    {group.groupName || group.name}
                                  </strong>
                                </span>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  <p className={styles.alertFooterQuestion}>
                    Do you still want to proceed?
                  </p>
                </div>
              ) : (
                <div
                  className="py-10 text-center flex flex-col items-center justify-center"
                  data-testid="customIcd-page-warning-standard-confirm"
                >
                  <div className="bg-slate-50 p-4 rounded-full mb-4">
                    <AlertTriangle className="h-8 w-8 text-slate-300" />
                  </div>
                  <p className="text-slate-600 text-sm px-4">
                    Confirm to proceed with{" "}
                    {warningDialog.type === "delete"
                      ? "deletion"
                      : "deactivation"}
                    .
                  </p>
                </div>
              ))}
          </div>

          <div className="p-6 pt-4 flex-shrink-0 bg-white border-t">
            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                data-testid="customIcd-page-warning-cancel-btn"
                onClick={() =>
                  setWarningDialog((prev) => ({ ...prev, open: false }))
                }
              >
                Cancel
              </Button>
              <Button
                data-testid="customIcd-page-warning-confirm-btn"
                className={`flex-1 rounded-xl h-10 text-white font-bold text-sm ${
                  warningDialog.type === "delete" ||
                  warningDialog.type === "hardDelete"
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-amber-500 hover:bg-amber-600"
                }`}
                onClick={() => {
                  const itemId = (warningDialog.item as any)?.id;
                  if (warningDialog.type === "delete") confirmDelete(itemId);
                  else if (warningDialog.type === "hardDelete")
                    hardDeleteItem(itemId);
                  else if (warningDialog.type === "update")
                    executeUpdate(itemId);
                  else confirmToggle(warningDialog.item!);
                  setWarningDialog((prev) => ({ ...prev, open: false }));
                }}
              >
                {warningDialog.type === "delete"
                  ? "Yes, Delete"
                  : warningDialog.type === "hardDelete"
                  ? "Yes, Permanently Delete"
                  : "Yes, Proceed"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster data-testid="customIcd-page-toaster" />

      <div
        className={styles.tableWrapper}
        data-testid="customIcd-page-table-wrapper"
      >
        <DataTable
          onAdd={handleAdd}
          onEdit={(item: any) => {
            if (item.isSoftDeleted) return;
            handleEdit(item);
          }}
          onDelete={(id) => {
            const item = allItems.find((i) => i.id === id);
            if (item?.isSoftDeleted) return;
            removeItem(id);
          }}
          data={allItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel="Add New Group"
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          recoveryActions={(item: any) => {
            if (!canRestoreOrHardDelete()) {
              return (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg"
                  data-testid={`customIcd-table-action-locked-${item.id}`}
                >
                  <i className="fa-solid fa-lock text-[15px]"></i>
                </span>
              );
            }
            return (
              <div
                className="flex items-center justify-center gap-1.5"
                data-testid={`customIcd-table-recovery-actions-${item.id}`}
              >
                <button
                  onClick={() => restoreItem(item.id)}
                  data-testid={`customIcd-table-action-restore-btn-${item.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <i className="fa-solid fa-rotate-left text-[9px]"></i>
                  Restore
                </button>
                <button
                  onClick={() =>
                    setWarningDialog({
                      open: true,
                      type: "hardDelete",
                      item,
                      usageData: null,
                      loadingUsage: false,
                    })
                  }
                  data-testid={`customIcd-table-action-harddelete-btn-${item.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all"
                >
                  <i className="fa-solid fa-trash text-[9px]"></i>
                  Delete
                </button>
              </div>
            );
          }}
          data-testid="customIcd-page-datatable"
        />
      </div>
    </div>
  );
};

export default CustomIcd;